/* eslint-disable no-nested-ternary */
import React from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import { SpecCardSkeleton, ProductCardSkeleton, ErrorBoundary } from '@sainsburys-tech/bolt'

import { contentAreaTypes } from '../../config'
import { getValue } from '../../utils/ConfigProvider'
import Templates from './ProductList.template'
import CategoryListSkeleton from '../CategoryList/CategoryListSkeleton'
import ProductListCard from './ProductListCard'

import * as Styled from './styles.js'

const CMS = loadable.lib(() => import(/* webpackChunkName: "cms-components" */ '@sainsburys-tech/bolt-cms-components'))

const ProductList = props => {
  const getProductCardLayout = (templateType, pageType) => {
    if (getValue('features.productList.forceClothingTemplate')) {
      return Templates.clothingLayout
    }

    if (pageType === Templates.pageTypeBrowse) {
      if (templateType === Templates.fashionTemplate) {
        return Templates.clothingLayout
      }
      if (templateType === Templates.whiteGoodTemplate) {
        return Templates.whiteGoodTemplate
      }
    }
    return Templates.defaultTemplate
  }

  const {
    citrus,
    currentPage,
    destinationPageType,
    isCannedSearch,
    isClearance,
    localisationPostcode,
    numberOfResults,
    pageSize,
    pageType,
    products,
    relavancyRecallOffset,
    searchTerm,
    sendCitrusClickBeacon,
    sendCitrusImpressionBeacon,
    showLoader,
    templateType,
    userState,
    allCookies
  } = props

  const layout = getProductCardLayout(templateType, pageType)

  const whiteGoodCategory = layout === Templates.whiteGoodTemplate
  const productCardClass = 'xs-6--none sm-4'
  const className = whiteGoodCategory ? 'xs-block xs-12--none md-12' : productCardClass

  const Skeleton = () => (
    <Styled.SkeletonWrapper className={className}>
      <ProductCardSkeleton isPortrait={layout === Templates.clothingLayout} mobileLayout='vertical' />
    </Styled.SkeletonWrapper>
  )
  const SpecSkeleton = () => (
    <Styled.SkeletonWrapper whiteGoods className={className}>
      <SpecCardSkeleton />
    </Styled.SkeletonWrapper>
  )

  const ProductCard = loadable(() => import(/* webpackChunkName: "labs-product-card" */ './StyledProductCard.js'), {
    fallback: <Skeleton />
  })

  return (
    <ErrorBoundary>
      <Styled.ProductList data-test='product-list'>
        {showLoader && destinationPageType === 'CLP' ? (
          <CategoryListSkeleton isFullSize />
        ) : (
          products.map((product, index) => {
            const partNumber = product.id
            const attributes = { ...product.attributes, partNumber }
            const productCardOnClick = () => {
              sendCitrusClickBeacon(attributes, citrus, partNumber)
            }

            if (product.type === contentAreaTypes.ContentInList) {
              return !showLoader ? (
                <ErrorBoundary>
                  <CMS>
                    {({ components }) => {
                      const { M053 } = components
                      return (
                        <Styled.LazyHydrateCard whenVisible style={{ display: 'flex' }} className={className}>
                          <ProductCard
                            as='div'
                            isM053
                            data-test='product-list-m053'
                            key={`M053-${index}`}
                            onClick={productCardOnClick}>
                            <M053 heading={product.heading} image={product.image} link={product.link} />
                          </ProductCard>
                        </Styled.LazyHydrateCard>
                      )
                    }}
                  </CMS>
                </ErrorBoundary>
              ) : !whiteGoodCategory ? (
                <Skeleton />
              ) : (
                <SpecSkeleton />
              )
            }

            return (
              <ErrorBoundary key={`${partNumber}-${index}-boundary`}>
                <ProductListCard
                  attributes={attributes}
                  citrus={citrus}
                  className={className}
                  currentPage={currentPage}
                  index={index}
                  isCannedSearch={isCannedSearch}
                  isClearance={isClearance}
                  layout={layout}
                  localisationPostcode={localisationPostcode}
                  numberOfResults={numberOfResults}
                  pageSize={pageSize}
                  pageType={pageType}
                  partNumber={partNumber}
                  productCardOnClick={productCardOnClick}
                  relavancyRecallOffset={relavancyRecallOffset}
                  searchTerm={searchTerm}
                  sendCitrusClickBeacon={sendCitrusClickBeacon}
                  sendCitrusImpressionBeacon={sendCitrusImpressionBeacon}
                  showLoader={showLoader}
                  templateType={templateType}
                  userState={userState}
                  whiteGoodCategory={whiteGoodCategory}
                  allCookies={allCookies}
                />
              </ErrorBoundary>
            )
          })
        )}
      </Styled.ProductList>
    </ErrorBoundary>
  )
}

ProductList.propTypes = {
  citrus: PropTypes.object.isRequired,
  currentPage: PropTypes.number,
  destinationPageType: PropTypes.string,
  isCannedSearch: PropTypes.bool,
  isClearance: PropTypes.bool,
  localisationPostcode: PropTypes.string,
  numberOfResults: PropTypes.number,
  pageSize: PropTypes.number,
  pageType: PropTypes.string,
  products: PropTypes.array.isRequired,
  relavancyRecallOffset: PropTypes.number,
  searchTerm: PropTypes.string,
  sendCitrusClickBeacon: PropTypes.func,
  sendCitrusImpressionBeacon: PropTypes.func.isRequired,
  showLoader: PropTypes.bool,
  templateType: PropTypes.string,
  userState: PropTypes.object,
  allCookies: PropTypes.object
}

ProductList.defaultProps = {
  currentPage: 1,
  destinationPageType: '',
  isCannedSearch: false,
  isClearance: false,
  localisationPostcode: '',
  numberOfResults: null,
  pageSize: 30,
  pageType: 'search',
  relavancyRecallOffset: 0,
  searchTerm: '',
  sendCitrusClickBeacon: () => {},
  showLoader: false,
  templateType: '',
  userState: {},
  allCookies: {}
}

export default ProductList
